// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Universe Loader
 * 
 * Central module for loading universe configurations.
 * Handles dynamic universe switching and data aggregation.
 */

import universeMilkyway from './universe-milkyway';
import universeDungeonDimensions from './universe-dungeon-dimensions';
import universeImprobability from './universe-improbability';
import universeDiscworld from './universe-discworld';
import universeLSpace from './universe-lspace';
import universePokemon from './universe-pokemon';
import universeNeonMetropolis from './universe-neonmetropolis';
import universeDigiworld from './universe-digiworld';
import universeMarioWorld from './universe-marioworld';
import universeSonicUniverse from './universe-sonicuniverse';
import universeZelda from './universe-zelda';
import universeStarWars from './universe-starwars';
import universeDoctorWho from './universe-doctorwho';
import universeStarTrek from './universe-startrek';
import universeMassEffect from './universe-masseffect';
import universeHalo from './universe-halo';
import universeWarhammer40k from './universe-warhammer40k';
import universeMarvel from './universe-marvel';
import universeDC from './universe-dc';

// Map of all available universes
const universeData = {
  milkyway: universeMilkyway,
  'dungeon-dimensions': universeDungeonDimensions,
  improbability: universeImprobability,
  discworld: universeDiscworld,
  lspace: universeLSpace,
  pokemon: universePokemon,
  neonmetropolis: universeNeonMetropolis,
  digiworld: universeDigiworld,
  marioworld: universeMarioWorld,
  sonicuniverse: universeSonicUniverse,
  zelda: universeZelda,
  starwars: universeStarWars,
  doctorwho: universeDoctorWho,
  startrek: universeStarTrek,
  masseffect: universeMassEffect,
  halo: universeHalo,
  warhammer40k: universeWarhammer40k,
  marvel: universeMarvel,
  dc: universeDC,
};

/**
 * Get all star systems and gates for a specific universe
 * @param {string} universeId - Universe identifier (milkyway, dungeon-dimensions, improbability, discworld, darksector)
 * @returns {object} Combined star systems and warp gates
 */
export const getUniverseData = (universeId = 'milkyway') => {
  const universe = universeData[universeId];
  
  if (!universe) {
    console.warn(`Universe ${universeId} not found, defaulting to milkyway`);
    return universeData.milkyway;
  }
  
  return universe;
};

/**
 * Get combined star systems and warp gates as a single array
 * Useful for rendering on the star map
 * @param {string} universeId - Universe identifier
 * @returns {array} Combined array of systems, stations, and gates
 */
export const getAllLocations = (universeId = 'milkyway') => {
  const universe = getUniverseData(universeId);
  
  // Combine star systems, space stations, and warp gates
  const locations = [
    ...universe.starSystems,
    ...(universe.spaceStations || []),
    ...universe.warpGates,
  ];
  
  return locations;
};

/**
 * Get combined positions for all locations (systems + stations + gates)
 * @param {string} universeId - Universe identifier
 * @returns {object} Combined position map
 */
export const getAllPositions = (universeId = 'milkyway') => {
  const universe = getUniverseData(universeId);
  
  return {
    ...universe.starPositions,
    ...(universe.stationPositions || {}),
    ...universe.warpGatePositions,
  };
};

/**
 * Find a location by ID across all types
 * @param {number|string} locationId - Location ID
 * @param {string} universeId - Universe identifier
 * @returns {object|null} Location object or null
 */
export const findLocation = (locationId, universeId = 'milkyway') => {
  const locations = getAllLocations(universeId);
  return locations.find(loc => loc.id === locationId) || null;
};

/**
 * Check if a location is a warp gate
 * @param {object} location - Location object
 * @returns {boolean} True if location is a warp gate
 */
export const isWarpGate = (location) => {
  return location && location.type === 'warpgate';
};

/**
 * Get warp gate by ID
 * @param {string} gateId - Gate identifier
 * @param {string} universeId - Universe identifier
 * @returns {object|null} Gate object or null
 */
export const getWarpGate = (gateId, universeId = 'milkyway') => {
  const universe = getUniverseData(universeId);
  return universe.warpGates.find(gate => gate.id === gateId) || null;
};

// Re-export universe metadata
export { universes, getUniverseInfo, isUniverseDiscovered, getDiscoveredUniverses } from './UniverseConfig';

// Export universe data for direct access if needed
export { universeMilkyway, universeDungeonDimensions, universeImprobability, universeDiscworld, universeLSpace, universePokemon, universeNeonMetropolis, universeDigiworld, universeMarioWorld, universeSonicUniverse, universeZelda, universeStarWars, universeDoctorWho, universeStarTrek };
